export type ColorSchemePreference = 'auto' | 'light' | 'dark';

/**
 * The DEVICE-LEVEL copy of the colour scheme (UX-930). Not per user, and never removed: it is what
 * paints before React mounts and what the login page uses, when there is no signed-in user to ask.
 * Once a user is known, their server-side setting (`util/color-scheme-setting.ts`) is the truth and
 * this key is rewritten to match it, so the next load in this browser paints right first time.
 * Sign-out leaves it alone, which is why the login page keeps the scheme last used here.
 */
export const COLOR_SCHEME_PREFERENCE_STORAGE_KEY = 'color-scheme-preference';

export function isColorSchemePreference(value: unknown): value is ColorSchemePreference {
    return value === 'auto' || value === 'light' || value === 'dark';
}

export function loadColorSchemePreference(): ColorSchemePreference {
    if (typeof window === 'undefined') {
        return 'auto';
    }
    try {
        const stored = window.localStorage.getItem(COLOR_SCHEME_PREFERENCE_STORAGE_KEY);
        return isColorSchemePreference(stored) ? stored : 'auto';
    } catch {
        return 'auto';
    }
}

export function saveColorSchemePreference(preference: ColorSchemePreference): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(COLOR_SCHEME_PREFERENCE_STORAGE_KEY, preference);
    } catch {
        // ignore quota / privacy errors
    }
}

export function resolveColorSchemeIsDark(
    preference: ColorSchemePreference,
    prefersDarkMode: boolean,
    brandDarkEnabled: boolean
): boolean {
    if (preference === 'dark') {
        return true;
    }
    if (preference === 'light') {
        return false;
    }
    return brandDarkEnabled && prefersDarkMode;
}

export function applyColorSchemeBodyClass(isDark: boolean): void {
    if (typeof document === 'undefined') {
        return;
    }
    const body = document.body;
    body.classList.add(isDark ? 'dark' : 'light');
    body.classList.remove(isDark ? 'light' : 'dark');
    // UX-930: the inline script in each index.html <head> paints <html> black for the first frame of
    // a Dark device. From here the class on <body> carries the scheme, so drop it: left in place it
    // would stop <body>'s own background reaching the canvas, and stay black after a switch to Light.
    document.documentElement.style.removeProperty('background-color');
}

/**
 * Apply the device-level forced preference before React mounts, so the first paint is not the
 * wrong scheme. Every shell entry (`index.tsx`, `studio.tsx`, `delivery.tsx`) calls it at module
 * scope. `auto` applies nothing here: it depends on the brand theme, which only React knows.
 */
export function applyStoredColorSchemeBodyClass(): void {
    const preference = loadColorSchemePreference();
    if (preference === 'dark') {
        applyColorSchemeBodyClass(true);
    } else if (preference === 'light') {
        applyColorSchemeBodyClass(false);
    }
}
